import { useEffect, useState } from 'react';
import { Loader, MaterialIcon, NoResults, SearchBar } from '@components';
import {
  Avatar,
  Paper,
  Stack,
  Table as MuiTable,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tooltip,
  Typography,
} from '@mui/material';
import { AponoIdentity, AponoIdentityAttribute, IdentitiesModel } from '@api';
import { ErrorPage } from '@pages/ErrorPage';
import { idpTypeIcon } from '@utils/identities';
import { useGetIdentities } from '@api/query/queries/identities';

export function IdentitiesPage() {
  const [filter, setFilter] = useState<string>('');
  const [filteredIdentities, setFilteredIdentities] = useState<AponoIdentity[]>([]);
  const { identities, isIdentitiesFetched } = useGetIdentities();

  useEffect(() => {
    if (identities && identities.identities.length > 0) {
      setFilteredIdentities(
        identities.identities.filter((identity) => {
          let isInContext = false;
          const isInName =
            identity.first_name?.toLowerCase().includes(filter) ||
            identity.last_name?.toLowerCase().includes(filter) ||
            identity.email?.toLowerCase().includes(filter);

          if (identity.attributes) {
            isInContext = Object.values(identity.attributes)
              .flat()
              ?.map((identityAttribute) => identityAttribute.name)
              .toString()
              .toLowerCase()
              .includes(filter);
          }

          return isInName || isInContext;
        }),
      );
    }
  }, [identities, filter]);

  if (!isIdentitiesFetched) {
    return <Loader absolute />;
  }

  if (identities && identities.identities?.length == 0) {
    return <ErrorPage errorCode={403} />;
  }

  const attributes = getAttributes(identities);

  return (
    <Stack spacing={2}>
      <SearchBar total={filteredIdentities.length} onChange={(value) => setFilter(value.toLowerCase())} />
      <Table identities={filteredIdentities || []} attributes={attributes} />
    </Stack>
  );
}

function Table({ identities, attributes }: { identities: Array<AponoIdentity>; attributes: Array<string> }) {
  if (identities.length === 0) {
    return <NoResults>No identities found</NoResults>;
  }

  return (
    <TableContainer component={Paper} elevation={0}>
      <MuiTable size="small">
        <THead columns={attributes} />
        <TableBody>
          {identities.map((identity) => (
            <TRow key={identity.email} identity={identity} attributes={attributes} />
          ))}
        </TableBody>
      </MuiTable>
    </TableContainer>
  );
}

function THead({ columns }: { columns: Array<string> }) {
  const tableColumns = ['Name', ...getColumnLabels(columns), ''];

  return (
    <TableHead>
      <TableRow>
        {tableColumns.map((column, index) => (
          <TableCell component="th" size="medium" key={`th-${index}`}>
            {column}
          </TableCell>
        ))}
      </TableRow>
    </TableHead>
  );
}

interface TRowProps {
  identity: AponoIdentity;
  attributes: Array<string>;
}

function TRow({ identity, attributes }: TRowProps) {
  const userName = `${identity.first_name} ${identity.last_name}`;
  const email = `${identity.email}`;
  return (
    <TableRow key={identity.id}>
      <TableCell width={314} height={56}>
        <Stack direction="row" spacing={2} alignItems="center">
          <IdentityIcon identity={identity} />
          <Stack>
            <Typography variant="subtitle2">{userName?.length > 1 ? userName : email}</Typography>
            <Typography variant="tooltip">{email}</Typography>
          </Stack>
        </Stack>
      </TableCell>
      {attributes.map((attributeType) => {
        return (
          <TableCell key={identity.id + attributeType} width={210}>
            <AttributeCell identity={identity} attributeType={attributeType} />
          </TableCell>
        );
      })}
      <TableCell></TableCell>
    </TableRow>
  );
}

const identitiesAttributesLabels: { [k: string]: string } = {
  user_groups: 'Groups',
  pagerduty_shift: 'PagerDuty Shifts',
  okta_manager: 'Manager ID',
  manager: 'Manager ID',
};

const identitiesAttributesHeaders: { [k: string]: string } = {
  user_groups: 'Groups',
  pagerduty_shift: 'PagerDuty Shifts',
  okta_manager: 'Manager',
  manager: 'Manager',
};

function getTooltip(attributeNames: Array<string>) {
  if (attributeNames) {
    const groupsTxt = `${attributeNames.length} Groups: `;
    return groupsTxt + attributeNames.toString();
  }
  return '';
}

function getAttributesCellLabel(firstItems: Array<string>, attributeNames: Array<string>, tooltip: string) {
  return (
    <>
      <Typography variant="inputLabel">{firstItems.toString()}</Typography>
      {attributeNames?.length > 0 ? (
        <Tooltip placement="top" arrow title={tooltip}>
          <Typography variant="inputLabel" color="text.highlight">
            {` +${attributeNames?.length} `}
          </Typography>
        </Tooltip>
      ) : null}
    </>
  );
}

function getAttributeTypes(types: Array<string>) {
  const attributeIds = types.filter((item) => item !== FIRST_COLUMN);
  attributeIds.unshift(FIRST_COLUMN);
  return attributeIds;
}

function getColumnLabels(types: Array<string>) {
  return types.map<string>((type) => identitiesAttributesHeaders[type]);
}

function IdentityIcon({ identity }: { identity: AponoIdentity }) {
  if (identity.idp_type && idpTypeIcon[identity.idp_type]) {
    return <Avatar src={idpTypeIcon[identity.idp_type]} sx={{ width: 24, height: 24 }} />;
  }
  return <MaterialIcon symbol={'person'} weight={700} gray />;
}

function AttributeCell({ attributeType, identity }: { attributeType: string; identity: AponoIdentity }) {
  const attributeItems: Array<AponoIdentityAttribute> = identity.attributes[attributeType];
  const attributeNames = Array.from(new Set(attributeItems?.map((it) => it.name)));
  const hasItems = attributeNames?.length > 0;

  const tooltip = getTooltip(attributeNames);
  const firstItems = attributeNames.splice(0, 2);
  const attributeCellLabel = getAttributesCellLabel(firstItems, attributeNames, tooltip);

  if (hasItems) {
    return (
      <Stack>
        <Typography variant="inputLabel">{attributeCellLabel}</Typography>
        <Typography variant="tooltip" color="text.disabled">
          {identitiesAttributesLabels[attributeType]}
        </Typography>
      </Stack>
    );
  } else {
    return (
      <Stack>
        <Typography variant="inputLabel">-</Typography>
        <Typography variant="tooltip" color="text.disabled">
          {identitiesAttributesLabels[attributeType]}
        </Typography>
      </Stack>
    );
  }
}

function getAttributes(identitiesModel?: IdentitiesModel) {
  if (!identitiesModel) {
    return [];
  }

  const attributesTypes = getAttributeTypes(identitiesModel.attributes);

  function doesAttributeTypeExistInIdentities(identities: Array<AponoIdentity>, attributeType: string) {
    return identities.some((identity) => {
      const att = identity.attributes[attributeType];
      return att && att.length > 0;
    });
  }

  return attributesTypes.filter((type) => {
    return doesAttributeTypeExistInIdentities(identitiesModel.identities, type);
  });
}

const FIRST_COLUMN = 'user_groups';
